import React from 'react';
import styled from 'astroturf/react';

import type { ArmSpec } from '../armRegistry';
import { readTrail } from '../memory';
import type { TrailEntry } from '../memory';

const MAX_VISIBLE_THUMBS = 4;
const FALLBACK_THUMBNAIL =
  'https://a.academia-assets.com/images/blank-paper.jpg';

function distinctWorks(): TrailEntry[] {
  const seen = new Set<number>();
  return readTrail().filter((entry) => {
    if (seen.has(entry.workId)) return false;
    seen.add(entry.workId);
    return true;
  });
}

function trailCopy(count: number): string {
  return (
    `You've read ${count} papers today. ` +
    'Sign up and start building your library.'
  );
}

const SessionTrailStrip: React.FC<{ works: TrailEntry[] }> = ({ works }) => {
  // The trail is newest-last and the copy singles out the current paper, so
  // the visible window comes from the END; older reads fold into the +N.
  const visible = works.slice(-MAX_VISIBLE_THUMBS);
  const overflow = works.length - visible.length;
  return (
    <Strip>
      <ThumbRow>
        {visible.map((work) => (
          <Thumb
            key={work.workId}
            src={work.thumbnailUrl || FALLBACK_THUMBNAIL}
            alt=""
          />
        ))}
        {overflow > 0 && <MoreCount>{`+${overflow}`}</MoreCount>}
      </ThumbRow>
      <TrailText>{trailCopy(works.length)}</TrailText>
    </Strip>
  );
};

export const sessionTrailSpec: ArmSpec = {
  stamp: () => ({
    render_state: distinctWorks().length >= 2 ? 'rendered' : 'fallback',
  }),
  slots: () => {
    const works = distinctWorks();
    if (works.length < 2) return undefined;
    return { preCta: <SessionTrailStrip works={works} /> };
  },
};

const Strip = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs-8, 8px);
  margin-bottom: var(--spacing-xs-16, 16px);
`;

const ThumbRow = styled.div`
  display: flex;
  gap: var(--spacing-xxs-4, 4px);
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Thumb = styled.img`
  width: 32px;
  height: 44px;
  flex: 0 0 auto;
  object-fit: cover;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-primary, #dddde2);
  border-radius: var(--border-radius-radius-xs, 2px);
`;

const MoreCount = styled.span`
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary, #6b6b76);
`;

const TrailText = styled.div`
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 13px;
  line-height: 18px;
  color: var(--text-primary, #26262b);
  text-align: center;
`;
